<template>
    <ideo-form-group>
        <ideo-form-group :label="$t('[[[Wybierz sklep]]]')">
            <div class="d-flex flex-column flex-md-row">
                <div class="flex-grow-1">
                    <ideo-autocomplete
                        v-model="form.settings.shopId"
                        name="shopId"
                        track-by="id"
                        :placeholder="$t('[[[zacznij pisać aby wyszukać sklep...]]]')"
                        :fetch="(id: number) => fetchShop(id)"
                        :search="(query: string, limit: number) => searchShop(query, limit)"
                    >
                        <template #selected="{ option }">
                            <div class="text-wrap">
                                {{ option.name }}
                            </div>
                        </template>
                        <template #option="{ option }">
                            <div class="text-wrap">
                                {{ option.name }}
                            </div>
                        </template>
                    </ideo-autocomplete>
                </div>
            </div>
        </ideo-form-group>

        <div class="row">
            <div class="col-md-6">
                <ideo-form-group :label="$t('[[[Publikuj od]]]')">
                    <ideo-datetime name="datePublishedUtc" v-model="form.settings.datePublishedUtcFrom" type="datetime" />
                </ideo-form-group>
            </div>
            <div class="col-md-6">
                <ideo-form-group :label="$t('[[[Publikuj do]]]')">
                    <ideo-datetime name="dateDismissedUtc" v-model="form.settings.datePublishedUtcDue" type="datetime" />
                </ideo-form-group>
            </div>
        </div>

        <ideo-form-group :label="$t('[[[Liczba wyświetlanych produktów]]]')" :invalid-feedback="form.$errors.first('settings.limit')" :state="form.$errors.state('settings.limit')">
            <ideo-form-select v-model="form.settings.limit" :options="displayOptions"></ideo-form-select>
        </ideo-form-group>
        <ideo-form-group :label="$t('[[[Szablon]]]')" :invalid-feedback="form.$errors.first('settings.format')" :state="form.$errors.state('settings.format')">
            <ideo-form-select v-model="form.settings.format" :options="formatOptions"></ideo-form-select>
        </ideo-form-group>
        <div class="pt-2 pb-3">
            <ideo-form-checkbox v-model="form.settings.isSale">
                {{ $t('[[[Produkty w promocji]]]') }}
            </ideo-form-checkbox>
        </div>

        <ideo-form-group :label="$t('[[[Wybierz etykiety produktów]]]')" :invalid-feedback="form.$errors.first('settings.labelsIds')" :state="form.$errors.first('settings.labelsIds')">
            <div class="d-flex flex-column flex-md-row">
                <div class="flex-grow-1">
                    <ideo-autocomplete
                        v-model="labelId"
                        name="labelId"
                        track-by="id"
                        :placeholder="$t('[[[zacznij pisać aby wyszukać etykietę...]]]')"
                        :fetch="(id: number) => fetchLabel(id)"
                        :search="(query: string, limit: number) => searchLabel(query, limit)"
                        :refresh="true"
                    >
                        <template #selected="{ option }">
                            <div class="text-wrap">
                                {{ option.name }}
                            </div>
                        </template>
                        <template #option="{ option }">
                            <div class="text-wrap">
                                {{ option.name }}
                            </div>
                        </template>
                    </ideo-autocomplete>
                </div>
                <div class="d-flex align-items-stretch">
                    <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addLabel()">
                        <i class="fas fa-plus"></i>
                    </button>
                </div>
            </div>
        </ideo-form-group>

        <h5 class="mb-3">{{ $t('[[[Etykiety]]]') }}</h5>
        <ideo-form-group v-if="labels">
            <div class="list-group">
                <div v-for="item in labels" :key="item.id" class="list-group-item list-group-item-action p-2 d-flex">
                    <div class="d-flex align-items-center justify-content-between gap-2 w-100">
                        <div class="d-flex align-items-center gap-2">
                            <i class="fad fa-fw fa-browser text-primary"></i>
                            <b :title="$t('[[[Nazwa]]]')">
                                {{ item.name }}
                            </b>
                        </div>

                        <div class="d-flex align-items-center">
                            <confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-label-${item.id}`" :value="item.id" @confirm="removeLabel(item.id)" />
                            <ideo-button variant="danger" size="sm" pill :id="`btn-delete-label-${item.id}`" :title="$t('[[[Usuń produkt]]]')">
                                <i class="fas fa-trash-alt"></i>
                            </ideo-button>
                        </div>
                    </div>
                </div>
                <div class="list-group-item list-group-item-light text-center" v-if="form.settings.labelsIds.length == 0">
                    {{ $t('[[[Brak wybranych etykiet]]]') }}
                </div>
            </div>
        </ideo-form-group>
    </ideo-form-group>
</template>

<script lang="ts" setup>
import { FormType } from '@/helpers/Form';
import { computed, onMounted, ref, watch } from 'vue';
import ShopsService, { AutocompleteListModel } from '@/modules/store/settings/shops/services/ShopsService';
import { $t } from '@/plugins/localization';
import { Option } from '@/helpers/Interfaces';
import LabelsService, { AutocompleteListModel as LabelListModel } from '@/modules/store/catalog/labels/services/LabelsService';
import { DateTime } from 'luxon';
import { computedAsync } from '@vueuse/core';

defineOptions({
    name: 'widget-tagged-products-settings'
});

interface FormSettings
{
    settings: {
        shopId: number,
        limit: string,
        format: string,
        datePublishedUtcFrom?: DateTime,
        datePublishedUtcDue?: DateTime,
        isSale: boolean,
        labelsIds: number[]
    };
}

const model = defineModel<FormType<FormSettings>>();
const defaults: FormSettings['settings'] = {
    shopId: 0,
    limit: '1',
    format: 'slider',
    datePublishedUtcFrom: null,
    datePublishedUtcDue: null,
    isSale: false,
    labelsIds: []
};

const form = computed(() => model.value);

onMounted(async () =>
{
    form.value.settings = {...defaults, ...form.value.settings};
});

const labelId = ref<number>(null);
const labels = computedAsync(async () =>
{
    try
    {
        if (form.value.settings.labelsIds.length === 0)
            return [];

        return await LabelsService.getListByIds(form.value.settings.labelsIds);
    }
    catch (ex)
    {
        return [];
    }
});

const displayOptions: Option[] = [
    { value: '1', text: $t('[[[1]]]') },
    { value: '2', text: $t('[[[2]]]') },
    { value: '3', text: $t('[[[3]]]') },
    { value: '4', text: $t('[[[4]]]') },
    { value: '5', text: $t('[[[5]]]') },
    { value: '6', text: $t('[[[6]]]') },
    { value: '7', text: $t('[[[7]]]') },
    { value: '8', text: $t('[[[8]]]') },
    { value: '9', text: $t('[[[9]]]') },
    { value: '10', text: $t('[[[10]]]') }
];

const formatOptions: Option[] = [
    { value: 'slider', text: $t('[[[slider]]]') },
    { value: 'grid', text: $t('[[[grid]]]') },
];

const fetchShop = async (id: number): Promise<AutocompleteListModel> =>
{
    const response = await ShopsService.fetchAutocompleteModel(id);

    return response.first() || null;
};

const searchShop = async (query: string, limit: number): Promise<AutocompleteListModel[]> =>
{
    return await ShopsService.getAutocompleteList(query, limit);
};

const fetchLabel = async (id: number): Promise<LabelListModel> =>
{
    const response = await LabelsService.fetchAutocompleteModel(id);

    return response.first() ?? null;
};

const searchLabel = async (query: string, limit: number): Promise<LabelListModel[]> =>
{
    return await LabelsService.getAutocompleteList(query, limit, form.value.settings.labelsIds);
};

const addLabel = async (): Promise<void> =>
{
    if (labelId.value == null)
        return;

    form.value.settings.labelsIds.push(labelId.value);
    labelId.value = null;
};

const removeLabel = async (id: number): Promise<void> =>
{
    form.value.settings.labelsIds = form.value.settings.labelsIds.filter(p => p !== id);
};

async function validate()
{
    form.value.validate(schema => ({
        settings: schema.object({
            shopId: schema.number().required($t('[[[Podanie sklepu jest wymagane]]]')).typeError($t('[[[Podanie sklepu jest wymagane]]]')),
            limit: schema.string().required($t('[[[Wybraniue limitu jest wymagane]]]')).typeError($t('[[[Wybraniue limitu jest wymagane]]]')),
            format: schema.string().required($t('[[[Wybraniue formatu jest wymagane]]]')).typeError($t('[[[Wybraniue formatu jest wymagane]]]'))
        })
    }));
}

watch(() => form.value.settings.shopId, () =>
{
    form.value.$errors.clear('settings.shopId');
});

watch(() => form.value.settings.limit, () =>
{
    form.value.$errors.clear('settings.limit');
});

watch(() => form.value.settings.format, () =>
{
    form.value.$errors.clear('settings.format');
});

defineExpose({
    validate
});
</script>
